import API from "../../services/api";

/**
 * @param {object} [options]
 * @param {number} [options.page] — when set, API returns { items, total, page, page_size }
 * @param {number} [options.page_size]
 * @param {string} [options.search]
 * @param {boolean} [options.active_only]
 * @param {string} [options.department_id]
 * @param {string} [options.designation_id]
 */
export const getEmployee = async (employeeId) => {
  const { data } = await API.get(`/employees/${employeeId}`);
  return data;
};

export const getEmployeeBankDetails = async (employeeId) => {
  const { data } = await API.get(`/employees/${employeeId}/bank-details`);
  return data;
};

export const getEmployees = async (options = {}) => {
  const params = {};
  if (options.page != null) params.page = options.page;
  if (options.page_size != null) params.page_size = options.page_size;
  if (options.search) params.search = options.search;
  if (options.active_only != null) params.active_only = options.active_only;
  if (options.department_id) params.department_id = options.department_id;
  if (options.designation_id) params.designation_id = options.designation_id;
  if (Array.isArray(options.employee_type) && options.employee_type.length) {
    params.employee_type = options.employee_type;
  }
  if (options.missing_verified_bank != null) {
    params.missing_verified_bank = options.missing_verified_bank;
  }

  const { data } = await API.get("/employees/", { params });
  return data;
};

export const exportEmployees = async (format = "csv", options = {}) => {
  const params = { format };
  if (options.active_only != null) params.active_only = options.active_only;
  if (Array.isArray(options.employee_type) && options.employee_type.length) {
    params.employee_type = options.employee_type;
  }
  const response = await API.get("/employees/export", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportEmployeesCsv = async (options = {}) => exportEmployees("csv", options);

export const exportEmployeesExcel = async (options = {}) => exportEmployees("xlsx", options);

export const getNextEmployeeCode = async (prefix, options = {}) => {
  const params = {};
  if (prefix) params.prefix = prefix;
  if (options.use_prefix === false) params.use_prefix = false;
  if (options.use_prefix === true) params.use_prefix = true;
  const { data } = await API.get("/employees/next-code", { params });
  return data;
};

export const getReportingManagers = async (excludeUserId = null) => {
  const params = excludeUserId ? { exclude_user_id: excludeUserId } : {};
  const { data } = await API.get("/reporting-managers/", { params });
  return data;
};

export const createEmployee = async (payload) => {
  const { data } = await API.post("/employees/", payload);
  return data;
};

/**
 * Atomic employee onboarding — single request for employee + optional
 * compensation, banking, and statutory. Server validates then commits in one txn.
 */
export const onboardEmployee = async (payload) => {
  const { data } = await API.post("/employees/onboard", payload);
  return data;
};

export const deleteEmployee = async (id) => {
  const { data } = await API.delete(`/employees/${id}`);
  return data;
};

export const updateEmployee = async (id, data) => {
  const { data: updatedEmployee } = await API.put(`/employees/${id}`, data);
  return updatedEmployee;
};

/** Explicit Admin action: send the welcome/login email again after a failed delivery. */
export const sendEmployeeWelcomeEmail = async (id) => {
  const { data } = await API.post(`/employees/${id}/welcome-email/send`);
  return data;
};

export const bulkUploadEmployees = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await API.post("/employees/bulk-upload", formData);

  return data;
};

export const fetchFKData = async () => {
  const [orgRes, deptRes, desigRes, locRes, mgrRes, payRes] = await Promise.all([
    API.get("/organisations/"),
    API.get("/departments/"),
    API.get("/designations/"),
    API.get("/locations/"),
    API.get("/employees/"),
    API.get("/pay_structures/"),
  ]);

  return {
    organisations: orgRes.data,
    departments: deptRes.data,
    designations: desigRes.data,
    locations: locRes.data,
    managers: mgrRes.data,
    pay_structures: payRes.data,
  };
};
